import { Box } from '@eatclubapp/ec-component-library';
import { useTheme } from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '../../../../../../components/Button';
import { Spacer } from '../../../../../../components/Spacer';
import { Typography } from '../../../../../../components/Typography';
import { updateBookingAction } from '../../../../../../data/actions/bookingAction';
import { bookingKeys, bookingPropTypes } from '../../../../../../data/models/Booking';
import { restaurantPropTypes } from '../../../../../../data/models/Restaurant';
import { getHexColourWithOpacity } from '../../../../../../theme';
import { trackEvent } from '../../../../../../utils/analytics';
import { formatMomentToDate } from '../../../../../../utils/helpers';
import { getContrastTextColor } from '../../../../../../utils/themeHelpers';
import useStyles from './DateStyles';

const Date = ({
  booking,
  disabled,
  isToday,
  restaurant,
  today,
  updateBooking,
  style,
  showDateInOneLine,
  dateFormat,
}) => {
  const theme = useTheme();
  const ref = useRef();
  const isSelected = booking.date && booking.date === formatMomentToDate(today);
  const classes = useStyles({ disabled, isSelected, showDateInOneLine });

  const isFull = false; // todo: get this from api

  useEffect(() => {
    if (ref && isSelected) {
      setTimeout(() => {
        ref.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }, 100);
    }
  }, [isSelected]);

  const getSelectedColor = () => {
    if (disabled) {
      return theme.colors.americanSilver;
    }

    if (isSelected) {
      return getHexColourWithOpacity(
        restaurant.data.availabilityFieldsActiveStateColour,
        restaurant.data.availabilityFieldsColourOpacity,
      );
    }

    return '';
  };

  const selectedColor = getSelectedColor();

  const handleOnClick = () => {
    trackEvent('Booking', 'Updating Date', 'Scroll');
    updateBooking(bookingKeys.date, formatMomentToDate(today));
    updateBooking(bookingKeys.selectedExperiences, []);
  };

  return (
    <Box ref={ref} style={style}>
      <Button
        disabled={disabled}
        onClick={handleOnClick}
        className={classes.button}
        style={{
          background: `${selectedColor}${isSelected ? ' !important' : ''}`,
          color: theme.colors.darkMode ? 'white' : getContrastTextColor(selectedColor),
          width: '100%',
        }}
        variant='outlined'
      >
        <Box className={classes.container}>
          <Box>
            {isToday ? (
              <Typography variant='caption' className={`${classes.heading} ${classes.todayPill}`}>
                Today
              </Typography>
            ) : (
              <Typography variant='caption' className={classes.heading}>
                {formatMomentToDate(today, 'ddd').toUpperCase()}
              </Typography>
            )}
          </Box>

          {showDateInOneLine ? (
            <Spacer direction='vertical' style={{ margin: 'auto' }}>
              <Typography>{`${formatMomentToDate(today, 'Do')} ${formatMomentToDate(
                today,
                'MMM YY',
              ).toUpperCase()}`}</Typography>
            </Spacer>
          ) : (
            <Spacer direction='vertical' style={{ margin: 'auto' }}>
              <Typography>{formatMomentToDate(today, 'Do')}</Typography>
              <Typography variant='caption' className={classes.month}>
                {formatMomentToDate(today, dateFormat ?? 'MMM').toUpperCase()}
              </Typography>
            </Spacer>
          )}
        </Box>

        {isFull && (
          <Typography className={classes.fullPill} variant='caption'>
            FULL
          </Typography>
        )}
      </Button>
    </Box>
  );
};

Date.defaultProps = {
  disabled: false,
  isToday: false,
  style: {},
  showDateInOneLine: false,
  dateFormat: null,
};

Date.propTypes = {
  booking: bookingPropTypes.isRequired,
  disabled: PropTypes.bool,
  isToday: PropTypes.bool,
  restaurant: restaurantPropTypes.isRequired,
  today: PropTypes.shape({ format: PropTypes.func.isRequired }).isRequired,
  updateBooking: PropTypes.func.isRequired,
  style: PropTypes.shape({}),
  showDateInOneLine: PropTypes.bool,
  dateFormat: PropTypes.string,
};

const mapStateToProps = (state) => ({
  booking: state.booking,
  restaurant: state.restaurant,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      updateBooking: updateBookingAction,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(Date);
